const discovered = [];

export default class GoogleAnalytics {
    _tracker;
    _trackIndex;

    /**
     * @param {*} tracker - The google analytics tracker object
     * @param {number} trackIndex - The trackIndex the tracker should start with
     */
    constructor(tracker, trackIndex = -1) {
        this._tracker = tracker;
        this._trackIndex = trackIndex;
    }

    get trackIndex() {
        return this._trackIndex;
    }

    /**
     * track an url or event
     * @param {string} type - The type of track
     * @param {*} payload - The tracking payload
     * @param {number} trackIndex - The index of the track event
     * @returns {Promise} - resolves when it is ok to continue tracking
     */
    track(type, payload, trackIndex) {
        this._trackIndex = trackIndex;
        if (type === 'event') {
            return this._trackEvent(
                payload.category, 
                payload.action, 
                payload.label, 
                payload.value, 
                payload.transport
            );
        } else if(type === 'page') {
            return this._trackPage(payload.url, payload.title);
        }
    }

    /**
     * track an url
     * @param {string} url - The url of the page
     * @param {string} title - The title of the page
     * @returns {Promise} - resolves when it is ok to continue tracking
     */
    _trackPage(url, title) {
        this._tracker.set('page', url);
        this._tracker.set('title', title);
        this._tracker.send('pageview');
        return Promise.resolve(true);
    }

    /**
     * track an event
     * @param {string} category - The category or type of event 
     * @param {string} action - The type of interaction that triggered the event
     * @param {string} label - An additional label for the event
     * @param {number} value - A numeric value associated with the event
     * @param {string} transport - Can be set to `beacon` to send tracking async via beacon
     * @returns {Promise} - resolves when it is ok to continue tracking
     */
    _trackEvent(category, action, label, value, transport = '') {
        this._tracker.send('event', {
            eventCategory: category,
            eventAction: action,
            eventLabel: label,
            eventValue: value,
            transport,
        });
        return Promise.resolve(true);
    }

    /**
     * destroy the tracker instance and clean up if necessary
     */
    destroy() {
        discovered.splice(discovered.indexOf(this._tracker.get('trackingId')), 1);
    }

    /**
     * repeat calls to this function will not discover the same tracker again
     * @param {number} trackIndex - The trackIndex the tracker should start with
     * @param {boolean} ignoreGTM - If `true` will ignore all GA trackers initialized by GTM
     * @returns - A promise resolving an array of wrapper instances for discovered tracker
     */
    static discover(trackIndex = -1, ignoreGTM = false) {
        return new Promise((resolve, reject) => {
            const ga = window.GoogleAnalyticsObject || 'ga';
            if (window[ga]) {
                const discoverTimeout = setTimeout(() => {
                    resolve([]);
                }, 100);
                window[ga](() => {
                    clearTimeout(discoverTimeout);
                    resolve(window[ga].getAll().map(tracker => {
                        const trackingId = tracker.get('trackingId');
                        const name = tracker.get('name');
                        if (
                            ~discovered.indexOf(trackingId)
                            || ignoreGTM && name.startsWith('gtm')
                        ) { return null; }
                        discovered.push(trackingId);
                        return new GoogleAnalytics(tracker, trackIndex);
                    }).filter(Boolean))
                });
            } else {
                resolve([])
            }
        });
    }

    /**
     * initialize general purpose things like the tracker's library js
     */
    static init() {
        return new Promise((resolve, reject) => {
            let script = document.querySelector('script[src*="google-analytics.com/analytics.js"]');
            const ga = window.GoogleAnalyticsObject || 'ga';
            // window.ga["ps_f"] gets set in loader/index.js, when we pre-create a potential ga
            // object to set our page path. this is necessary for embedded tracking, because the
            // initial google-initiated track wouldn't include our #! page path otherwise. however,
            // if no GA tracker is created by the hosting page, and we want to create our own, we
            // need to make sure that our own placeholder window.ga from loader/index.js doesn't
            // make us believe that a GA tracker is already instantiated.
            if ((!window[ga] || window[ga]["ps_f"] === true) && !script) {
                if (window[ga] && window[ga]["ps_f"]) delete window[ga]["ps_f"];
                (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
                (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
                m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
                })(window,document,'script','//www.google-analytics.com/analytics.js','ga');
                script = document.querySelector('script[src*="google-analytics.com/analytics.js"]');
            } 
            
            if (script) {
                if(window.google_tag_data) {
                    //script is already loaded
                    resolve();
                } else {
                    script.addEventListener('load', () => {
                        resolve();
                    })
                }
            } else {
                resolve();
            }
        })
    }

    /**
     * @param {*} options - The tracker options 
     * @returns - A promise resolving the wrapper instance
     */
    static create({code, scope, params = {}}) {
        return new Promise((resolve, reject) => {
            if (~discovered.indexOf(code)) {
                resolve(null);
                return;
            }
            const ga = window.GoogleAnalyticsObject || 'ga';
            const name = `${scope}_${code}`;
            window[ga]('create', code, 'auto', name);
            window[ga](() => {
                const tracker = window[ga].getByName(name);
                if (tracker) {
                    tracker.set('anonymizeIp', true);
                    for(let key in params) {
                        tracker.set(key, params[key]);
                    }
                    discovered.push(code);
                    resolve(new GoogleAnalytics(tracker))
                } else {
                    resolve(null);
                }
            })
        })
    }
}


// WEBPACK FOOTER //
// ./lib/analytics/tracker-googleanalytics.js